import { Fragment, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, BarChart3, Plug, Plus, Sparkles, Trash2 } from 'lucide-react';

import { listDepartments } from '@/api/employees.api';
import { listSeats } from '@/api/seats.api';
import { listIntegrations, listProviderCatalog } from '@/api/integrations.api';
import {
  createMetric,
  deleteMetric,
  deleteMetricEntry,
  listMetricEntries,
  listMetrics,
  upsertMetricEntry,
  type Metric,
  type MetricDataSourceConfig,
  type MetricEntry,
} from '@/api/scorecards.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { metricStatus, type MetricStatus } from '@/lib/scorecards/status';
import { rollingMondays, shortMonthDay, toIsoDate } from '@/lib/scorecards/weeks';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * Scorecards — Phase 11 Day 1.
 *
 * EOS-style 13-week rolling grid. Manual entry only on Day 1; the
 * integration provider abstraction (ShipEdge first, Gusto + QuickBooks
 * later) lands in subsequent days.
 *
 * Layout:
 *   - Sticky left column: metric title + goal + unit. Trash icon (admin)
 *     soft-removes the row.
 *   - 13 columns of weeks, oldest week on the left, current Monday on
 *     the right. Each cell is either empty or a colored value
 *     (green/yellow/red).
 *   - Click a cell to edit (admin-only). Numeric input + Enter saves
 *     via PUT-upsert. Esc cancels. Empty value clears the cell.
 *
 * Anyone in the org can read the grid. Cell editing + Add Metric +
 * Hide are visionary/integrator only — gated client-side AND
 * server-side. Read-only viewers still see the colors and values.
 */

type GroupBy = 'none' | 'seat' | 'department';

interface MetricGroup {
  key: string;
  /** Header text; empty string for the single "none" group. */
  label: string;
  metrics: Metric[];
}

/** Bucket key for metrics with no seat / no department. */
const UNGROUPED = '__ungrouped__';

export function ScorecardsPage() {
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const canManage =
    authState.status === 'authenticated' &&
    ['visionary', 'integrator'].includes(authState.me.membership.role);

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Metric | null>(null);
  const [groupBy, setGroupBy] = useState<GroupBy>('none');

  // Compute the rolling-13 axis once per page mount. The user can stay
  // on the page across midnight without breaking display; the column
  // labels won't shift mid-session, which is a feature (we'd rather
  // they refresh deliberately than have the grid flicker).
  const weeks = useMemo(() => rollingMondays(), []);

  const metricsQuery = useQuery({
    queryKey: ['scorecards', 'metrics'],
    queryFn: () => listMetrics(),
  });

  const metrics = useMemo(() => metricsQuery.data ?? [], [metricsQuery.data]);

  // Fan-out one entries query per metric. TanStack Query handles the
  // parallelism; useQueries cleans up automatically when metrics change.
  // For ~10 metrics that's 10 small GET requests on first load — fine
  // for now. If a tenant grows past ~50 metrics we'll batch via a single
  // /entries?metricIds=... endpoint.
  const entriesQueries = useQueries({
    queries: metrics.map((m) => ({
      queryKey: ['scorecards', 'entries', m.id],
      queryFn: () => listMetricEntries(m.id),
    })),
  });

  // Build a Map<metricId, Map<weekIso, MetricEntry>> for cheap cell lookups.
  const entriesByMetric = useMemo(() => {
    const out = new Map<string, Map<string, MetricEntry>>();
    metrics.forEach((m, i) => {
      const data = entriesQueries[i]?.data ?? [];
      const inner = new Map<string, MetricEntry>();
      for (const e of data) {
        // Normalize the server's ISO timestamp to the YYYY-MM-DD
        // form used as the column key.
        inner.set(e.weekOf.slice(0, 10), e);
      }
      out.set(m.id, inner);
    });
    return out;
  }, [metrics, entriesQueries]);

  // Department labels for the "group by department" view. Lazy — only
  // fetched when that grouping is selected. Seat labels ride along on
  // each metric's `seat` relation, so no extra fetch there.
  const departmentsQuery = useQuery({
    queryKey: ['departments'],
    queryFn: listDepartments,
    enabled: groupBy === 'department',
  });
  const departmentNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of departmentsQuery.data ?? []) map.set(d.id, d.name);
    return map;
  }, [departmentsQuery.data]);

  // Partition the flat metric list into display groups. "None" is a single
  // unlabeled group (preserves the original flat table); Seat/Department
  // bucket by the metric's seatId/departmentId with a trailing "No …" group.
  const groups = useMemo<MetricGroup[]>(() => {
    if (groupBy === 'none') {
      return [{ key: 'all', label: '', metrics }];
    }
    const buckets = new Map<string, Metric[]>();
    for (const m of metrics) {
      const key = (groupBy === 'seat' ? m.seatId : m.departmentId) ?? UNGROUPED;
      const bucket = buckets.get(key);
      if (bucket) bucket.push(m);
      else buckets.set(key, [m]);
    }
    const out: MetricGroup[] = [];
    for (const [key, groupMetrics] of buckets) {
      let label: string;
      if (key === UNGROUPED) {
        label = groupBy === 'seat' ? 'No seat' : 'No department';
      } else if (groupBy === 'seat') {
        label = groupMetrics[0]?.seat?.name ?? 'No seat';
      } else {
        label = departmentNameById.get(key) ?? 'Department';
      }
      out.push({ key, label, metrics: groupMetrics });
    }
    // Named groups first (alphabetical), the "No …" bucket always last.
    out.sort((a, b) => {
      const aUngrouped = a.key === UNGROUPED;
      const bUngrouped = b.key === UNGROUPED;
      if (aUngrouped !== bUngrouped) return aUngrouped ? 1 : -1;
      return a.label.localeCompare(b.label);
    });
    return out;
  }, [groupBy, metrics, departmentNameById]);

  const columnCount = 1 + weeks.length + (canManage ? 1 : 0);

  function invalidateMetric(metricId: string): Promise<void> {
    return queryClient
      .invalidateQueries({ queryKey: ['scorecards', 'entries', metricId] })
      .then(() => undefined);
  }
  function invalidateAll(): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: ['scorecards'] }).then(() => undefined);
  }

  // Cell upsert mutation. We don't optimistically pre-write because the
  // server returns the canonical row (with computed status) — settling
  // on the response keeps the cell color in sync without a re-fetch.
  const upsertMutation = useMutation({
    mutationFn: ({ metricId, weekOf, value }: { metricId: string; weekOf: string; value: number }) =>
      upsertMetricEntry(metricId, { weekOf, value }),
    onSuccess: (_data, { metricId }) => {
      void invalidateMetric(metricId);
    },
    onError: (err) => {
      const code = mutationErrorCode(err);
      if (code === 'invalid_week_of') {
        toast.show("That date doesn't fall on a Monday — refresh and try again.", {
          variant: 'warning',
        });
      } else {
        toast.show('Could not save the value — try again.', { variant: 'danger' });
      }
    },
  });

  const clearMutation = useMutation({
    mutationFn: ({ metricId, weekOf }: { metricId: string; weekOf: string }) =>
      deleteMetricEntry(metricId, weekOf),
    onSuccess: (_data, { metricId }) => {
      void invalidateMetric(metricId);
    },
  });

  const deleteMetricMutation = useMutation({
    mutationFn: (metricId: string) => deleteMetric(metricId),
    onSuccess: () => {
      void invalidateAll();
      setPendingDelete(null);
      toast.show('Metric removed from the active grid', { variant: 'success' });
    },
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="typo-h1">Scorecards</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            13-week rolling grid. Click a cell to record this week&apos;s number; colors compare against each
            metric&apos;s goal.
          </p>
        </div>
        {canManage ? (
          <div className="flex flex-wrap gap-2">
            <Link
              to="/settings/integrations"
              className="inline-flex items-center gap-1.5 rounded-kw-md border border-kw-border bg-white px-3 py-2 text-[13px] font-medium text-kw-shark shadow-kw-xs hover:border-kw-cerulean/40 hover:text-kw-cerulean"
            >
              <Plug className="size-4" />
              Integrations
            </Link>
            <Button
              onClick={() => {
                setCreateOpen(true);
              }}
            >
              <Plus className="size-4" />
              Add metric
            </Button>
          </div>
        ) : null}
      </div>

      {!metricsQuery.isLoading && !metricsQuery.error && metrics.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] font-medium text-kw-muted">Group by</span>
          <div className="inline-flex rounded-kw-md border border-kw-border bg-white p-0.5">
            {(
              [
                ['none', 'None'],
                ['seat', 'Seat'],
                ['department', 'Department'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={groupBy === value}
                onClick={() => {
                  setGroupBy(value);
                }}
                className={`rounded-[6px] px-2.5 py-1 text-[12px] font-medium transition-colors ${
                  groupBy === value ? 'bg-kw-cerulean text-white' : 'text-kw-shark hover:bg-kw-bg'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {metricsQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : metricsQuery.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load metrics</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {metricsQuery.error instanceof Error ? metricsQuery.error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : metrics.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <BarChart3 className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No metrics on the scorecard yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              {canManage
                ? 'Add a metric and start filling in weekly numbers — colors will surface trends as soon as you have a few weeks.'
                : 'An admin can add the first metric.'}
            </p>
          </div>
          {canManage ? (
            <Button
              onClick={() => {
                setCreateOpen(true);
              }}
            >
              <Sparkles className="size-4" />
              Add your first metric
            </Button>
          ) : null}
        </Card>
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-kw-border bg-kw-bg">
                <th className="sticky left-0 z-10 min-w-[220px] bg-kw-bg px-3 py-2 text-left font-medium uppercase tracking-wide text-kw-muted">
                  Metric
                </th>
                {/* Week columns rendered oldest → newest so the eye reads
                    left-to-right as time progresses. `rollingMondays`
                    returns newest-first, so reverse here for display. */}
                {[...weeks].reverse().map((d) => (
                  <th
                    key={toIsoDate(d)}
                    className="border-l border-kw-border px-2 py-2 text-center font-medium uppercase tracking-wide text-kw-muted"
                    title={toIsoDate(d)}
                  >
                    {shortMonthDay(d)}
                  </th>
                ))}
                {canManage ? (
                  <th className="border-l border-kw-border px-2 py-2" aria-label="Actions" />
                ) : null}
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <Fragment key={group.key}>
                  {groupBy !== 'none' ? (
                    <tr className="border-b border-kw-border bg-kw-bg">
                      <th
                        scope="colgroup"
                        colSpan={columnCount}
                        className="sticky left-0 z-10 bg-kw-bg px-3 py-1.5 text-left align-middle text-[11px] font-semibold uppercase tracking-wide text-kw-shark"
                      >
                        {group.label}
                        <span className="ml-2 font-normal normal-case tracking-normal text-kw-muted">
                          {group.metrics.length} metric{group.metrics.length === 1 ? '' : 's'}
                        </span>
                      </th>
                    </tr>
                  ) : null}
                  {group.metrics.map((m) => {
                    const entries = entriesByMetric.get(m.id) ?? new Map<string, MetricEntry>();
                    return (
                      <tr key={m.id} className="border-b border-kw-border last:border-b-0">
                        <th
                          scope="row"
                          className="sticky left-0 z-10 min-w-[220px] border-r border-kw-border bg-white px-3 py-2 text-left align-top"
                        >
                          <div className="flex items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-[13px] font-medium text-kw-shark">{m.title}</p>
                              <p className="text-[11px] text-kw-muted">
                                Goal {formatValue(m.goal, m)}{' '}
                                <span className="text-kw-ghost">
                                  ({m.direction === 'higher_is_better' ? '↑' : '↓'})
                                </span>
                              </p>
                              {m.ownerEmployee ? (
                                <p className="text-[11px] text-kw-muted">
                                  {m.ownerEmployee.firstName} {m.ownerEmployee.lastName}
                                </p>
                              ) : null}
                              {m.seat ? (
                                <p className="mt-0.5">
                                  <span className="inline-flex items-center rounded-kw-full bg-kw-cerulean-light px-1.5 py-0.5 text-[10px] font-medium text-kw-cerulean-dark">
                                    Seat: {m.seat.name}
                                  </span>
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </th>
                        {[...weeks].reverse().map((d) => {
                          const iso = toIsoDate(d);
                          const entry = entries.get(iso);
                          return (
                            <td key={iso} className="border-l border-kw-border p-0 text-center align-middle">
                              <Cell
                                metric={m}
                                weekOf={iso}
                                entry={entry}
                                canEdit={canManage}
                                onSave={(value) => {
                                  upsertMutation.mutate({ metricId: m.id, weekOf: iso, value });
                                }}
                                onClear={() => {
                                  clearMutation.mutate({ metricId: m.id, weekOf: iso });
                                }}
                              />
                            </td>
                          );
                        })}
                        {canManage ? (
                          <td className="border-l border-kw-border px-2 py-2 text-center">
                            <button
                              type="button"
                              aria-label={`Remove ${m.title}`}
                              onClick={() => {
                                setPendingDelete(m);
                              }}
                              className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <CreateMetricModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
        }}
        onCreated={() => {
          void invalidateAll();
        }}
      />

      <Modal
        open={Boolean(pendingDelete)}
        onClose={() => {
          setPendingDelete(null);
        }}
        title="Remove metric from grid"
        size="sm"
      >
        {pendingDelete ? (
          <div className="space-y-4">
            <p className="text-[13px] text-kw-shark">
              Hide <strong>{pendingDelete.title}</strong> from the active scorecard?
            </p>
            <p className="text-[12px] text-kw-muted">
              The metric and its history aren&apos;t deleted — they&apos;re hidden from the grid. An admin can
              restore them later.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setPendingDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                isLoading={deleteMetricMutation.isPending}
                onClick={() => {
                  deleteMetricMutation.mutate(pendingDelete.id);
                }}
              >
                Hide metric
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Cell
// ─────────────────────────────────────────────────────────────────────────

function Cell({
  metric,
  weekOf,
  entry,
  canEdit,
  onSave,
  onClear,
}: {
  metric: Metric;
  weekOf: string;
  entry: MetricEntry | undefined;
  canEdit: boolean;
  onSave: (value: number) => void;
  onClear: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => (entry ? String(entry.value) : ''));

  // Recompute status from the live entry so an optimistic refetch
  // updates the color the moment the server responds.
  const status: MetricStatus | null = entry ? metricStatus(entry.value, metric.goal, metric.direction) : null;

  function commit(): void {
    const trimmed = draft.trim();
    if (trimmed === '') {
      setEditing(false);
      if (entry) onClear();
      return;
    }
    const value = Number(trimmed);
    if (!Number.isFinite(value)) {
      setEditing(false);
      return;
    }
    if (value === entry?.value) {
      setEditing(false);
      return;
    }
    onSave(value);
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        type="number"
        step="any"
        autoFocus
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            setDraft(entry ? String(entry.value) : '');
            setEditing(false);
          }
        }}
        className="block w-full bg-white px-2 py-1.5 text-center text-[12px] text-kw-shark focus:outline-none focus:ring-2 focus:ring-kw-cerulean/40"
      />
    );
  }

  // Read-only visual: empty = neutral; populated = colored chip.
  const tone =
    status === 'green'
      ? 'bg-kw-emerald-light text-kw-tag-text'
      : status === 'yellow'
        ? 'bg-kw-warning-bg text-kw-warning-text'
        : status === 'red'
          ? 'bg-kw-danger-bg text-kw-danger-text'
          : 'text-kw-ghost';

  const display = entry ? formatValue(entry.value, metric) : '—';
  const label = entry
    ? `${metric.title} for week of ${weekOf}: ${display} (${status ?? 'unknown'})`
    : `${metric.title} for week of ${weekOf}: empty`;

  if (!canEdit) {
    return (
      <div
        aria-label={label}
        className={`flex h-full min-h-[2.5rem] items-center justify-center px-2 py-1.5 text-[12px] font-medium ${tone}`}
      >
        {display}
      </div>
    );
  }

  return (
    <button
      type="button"
      aria-label={label}
      onClick={() => {
        setDraft(entry ? String(entry.value) : '');
        setEditing(true);
      }}
      className={`flex h-full min-h-[2.5rem] w-full items-center justify-center px-2 py-1.5 text-[12px] font-medium transition-colors hover:ring-2 hover:ring-kw-cerulean/40 ${tone}`}
    >
      {display}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Add metric modal
// ─────────────────────────────────────────────────────────────────────────

function CreateMetricModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState('');
  const [unit, setUnit] = useState<'count' | 'dollars' | 'percent' | 'ratio'>('count');
  const [direction, setDirection] = useState<'higher_is_better' | 'lower_is_better'>('higher_is_better');
  const [goal, setGoal] = useState('');
  // Optional seat this measurable is accountable to (ADR-028).
  const [seatId, setSeatId] = useState('');

  // Current-chart seats for the picker. Lazy — only fetched once the
  // modal opens.
  const seatsQuery = useQuery({
    queryKey: ['seats', { scenario: 'current' }],
    queryFn: () => listSeats({ scenario: 'current' }),
    enabled: open,
  });

  // Phase 11 Day 2: data-source picker. The pair (sourceProvider,
  // sourceMetricKey) populates `dataSourceConfig`. Empty
  // sourceProvider = manual entry.
  const [sourceProvider, setSourceProvider] = useState('');
  const [sourceMetricKey, setSourceMetricKey] = useState('');

  // Pull the catalog + the org's connections so the picker shows
  // only providers the org has actually connected. Lazy-loaded
  // (only fetches when the modal first opens).
  const catalogQuery = useQuery({
    queryKey: ['integrations', 'catalog'],
    queryFn: listProviderCatalog,
    enabled: open,
  });
  const integrationsQuery = useQuery({
    queryKey: ['integrations', 'connections'],
    queryFn: listIntegrations,
    enabled: open,
  });

  const connectedProviderIds = useMemo(
    () => new Set((integrationsQuery.data ?? []).filter((i) => i.status === 'active').map((i) => i.provider)),
    [integrationsQuery.data],
  );
  const availableProviders = useMemo(
    () => (catalogQuery.data ?? []).filter((p) => p.isImplemented && connectedProviderIds.has(p.id)),
    [catalogQuery.data, connectedProviderIds],
  );
  const selectedProvider = useMemo(
    () => availableProviders.find((p) => p.id === sourceProvider),
    [availableProviders, sourceProvider],
  );

  const mutation = useMutation({
    mutationFn: () => {
      const dataSourceConfig: MetricDataSourceConfig =
        sourceProvider && sourceMetricKey
          ? { kind: 'integration', providerId: sourceProvider, metricKey: sourceMetricKey }
          : { kind: 'manual' };
      return createMetric({
        title: title.trim(),
        unit,
        direction,
        goal: Number(goal),
        seatId: seatId || undefined,
        dataSourceConfig,
      });
    },
    onSuccess: () => {
      onCreated();
      onClose();
      setTitle('');
      setUnit('count');
      setDirection('higher_is_better');
      setGoal('');
      setSeatId('');
      setSourceProvider('');
      setSourceMetricKey('');
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!title.trim() || goal.trim() === '') return;
    const goalNum = Number(goal);
    if (!Number.isFinite(goalNum)) return;
    if (sourceProvider && !sourceMetricKey) return; // chose a provider but no key
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Add metric" size="sm">
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="space-y-4"
      >
        <p className="text-[13px] text-kw-muted">
          One row on the scorecard. Day-1 ships manual weekly entry; auto-population from integrations
          (ShipEdge, Gusto, QuickBooks) lands in subsequent days.
        </p>

        <Input
          label="Title"
          name="title"
          required
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          placeholder="e.g. Orders shipped"
          hint="Unique per org. Shown as the row label."
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-medium text-kw-muted">Unit</span>
            <select
              value={unit}
              onChange={(e) => {
                setUnit(e.target.value as typeof unit);
              }}
              className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
            >
              <option value="count">Count</option>
              <option value="dollars">Dollars ($)</option>
              <option value="percent">Percent (%)</option>
              <option value="ratio">Ratio (0–1)</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-medium text-kw-muted">Direction</span>
            <select
              value={direction}
              onChange={(e) => {
                setDirection(e.target.value as typeof direction);
              }}
              className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
            >
              <option value="higher_is_better">Higher is better</option>
              <option value="lower_is_better">Lower is better</option>
            </select>
          </label>
        </div>

        <Input
          label="Goal"
          name="goal"
          type="number"
          required
          value={goal}
          onChange={(e) => {
            setGoal(e.target.value);
          }}
          placeholder="e.g. 100"
          hint="Numbers within 20% of goal show yellow; beyond that is red."
        />

        {/* Seat picker — ADR-028. Optional; assigns accountability for the
            number to a seat so it shows on that seat's scorecard. */}
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-kw-muted">Seat</span>
          <select
            value={seatId}
            onChange={(e) => {
              setSeatId(e.target.value);
            }}
            className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          >
            <option value="">No seat</option>
            {(seatsQuery.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.holderEmployee ? ` — ${s.holderEmployee.firstName} ${s.holderEmployee.lastName}` : ''}
              </option>
            ))}
          </select>
          <span className="text-[11px] text-kw-muted">
            Optional. The seat accountable for this measurable — shows on that seat&apos;s scorecard.
          </span>
        </label>

        {/* Data-source picker — Phase 11 Day 2. Manual is always the
            top option. Connected providers append below. If none are
            connected, the picker is hidden and a small CTA points to
            the integrations page. */}
        <fieldset className="space-y-2 rounded-kw-md border border-kw-border bg-kw-bg p-3">
          <legend className="px-1 text-[12px] font-medium text-kw-muted">Data source</legend>

          {availableProviders.length === 0 ? (
            <p className="text-[12px] text-kw-muted">
              Manual entry. To auto-populate from a connected system,{' '}
              <Link to="/settings/integrations" className="text-kw-cerulean hover:underline">
                connect an integration
              </Link>
              .
            </p>
          ) : (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wide text-kw-muted">Source</span>
                <select
                  value={sourceProvider}
                  onChange={(e) => {
                    setSourceProvider(e.target.value);
                    // Reset the metric-key when the provider changes.
                    setSourceMetricKey('');
                  }}
                  className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
                >
                  <option value="">Manual entry</option>
                  {availableProviders.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}
                    </option>
                  ))}
                </select>
              </label>

              {selectedProvider ? (
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] uppercase tracking-wide text-kw-muted">
                    {selectedProvider.displayName} metric
                  </span>
                  <select
                    value={sourceMetricKey}
                    onChange={(e) => {
                      setSourceMetricKey(e.target.value);
                      // Auto-suggest a sensible default direction.
                      const m = selectedProvider.availableMetrics.find((x) => x.key === e.target.value);
                      if (m) setDirection(m.defaultDirection);
                    }}
                    className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
                  >
                    <option value="">Pick a metric…</option>
                    {selectedProvider.availableMetrics.map((m) => (
                      <option key={m.key} value={m.key}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              <p className="flex items-start gap-1.5 text-[11px] text-kw-muted">
                <Plug className="mt-0.5 size-3 shrink-0" />
                <span>
                  Pulled from {selectedProvider?.displayName ?? 'the connected provider'} on every sync. You
                  can still override individual cells manually.
                </span>
              </p>
            </>
          )}
        </fieldset>

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">{describeCreateError(mutation.error)}</p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!title.trim() || goal.trim() === ''}>
            Add metric
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

function formatValue(value: number, metric: Pick<Metric, 'unit' | 'unitLabel'>): string {
  switch (metric.unit) {
    case 'dollars':
      return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      });
    case 'percent':
      return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    case 'ratio':
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    case 'custom':
      return `${value.toLocaleString()} ${metric.unitLabel ?? ''}`.trim();
    case 'count':
    default:
      return value.toLocaleString();
  }
}

function mutationErrorCode(err: unknown): string | null {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: unknown } } }).response;
    const code = response?.data?.error;
    if (typeof code === 'string') return code;
  }
  return null;
}

function describeCreateError(err: unknown): string {
  const code = mutationErrorCode(err);
  if (code === 'title_taken') return 'A metric with that title already exists in your org.';
  if (code === 'validation_failed') return 'Some fields look invalid — double-check and retry.';
  return err instanceof Error ? err.message : 'Could not add metric';
}
